/**
 * Anmeldung mit Fingerabdruck oder Gesicht — die Regeln ohne Brücke und ohne DOM
 * (Native-Plugin `Biometrie`, docs/android-app-vertrag.md). `biometrie.ts`
 * spricht mit der Hülle, AuthScreen und App-Info fragen hier, was zu zeigen ist.
 */

/** Warum die Biometrie (nicht) geht — `BiometricManager.canAuthenticate(BIOMETRIC_WEAK)`. */
export type BiometrieGrund = 'ok' | 'keine_hardware' | 'nicht_eingerichtet' | 'nicht_verfuegbar';

/** Antwort von `pruefen()`. */
export interface BiometrieStand {
    verfuegbar: boolean;
    grund: BiometrieGrund;
    /** Eine Anmeldung liegt auf diesem Gerät. */
    gespeichert: boolean;
    /** Für wen (Klartext, nur zur Anzeige); `null` ohne Eintrag. */
    email: string | null;
}

/**
 * Ablehnungscodes des Plugins; alles Unbekannte zählt als `fehler`.
 * `biometrie_geaendert`: der Wächter-Schlüssel im Keystore meldet neue oder entfernte Fingerabdrücke/Gesichter —
 * das Plugin hat den Eintrag gelöscht (BiometriePlugin.java, „Wächter").
 */
export type BiometrieFehler =
    | 'abgebrochen' | 'gesperrt' | 'nicht_gespeichert' | 'nicht_verfuegbar' | 'schluessel_ungueltig' | 'biometrie_geaendert' | 'fehler';

const FEHLER: readonly BiometrieFehler[] = [
    'abgebrochen', 'gesperrt', 'nicht_gespeichert', 'nicht_verfuegbar', 'schluessel_ungueltig', 'biometrie_geaendert', 'fehler',
];

/** Code aus einer Capacitor-Ablehnung (`{ code }`). */
export const biometrieFehlerAus = (e: unknown): BiometrieFehler => {
    const code = typeof e === 'object' && e !== null ? (e as { code?: unknown }).code : undefined;
    return typeof code === 'string' && (FEHLER as readonly string[]).includes(code) ? code as BiometrieFehler : 'fehler';
};

/** Klartext zu `biometrie_geaendert`. */
export const BIOMETRIE_GEAENDERT_TEXT =
    'Die Fingerabdrücke oder Gesichter auf diesem Gerät haben sich geändert. Bitte einmal mit Passwort anmelden und die Biometrie neu einrichten.';

/** Klartext zum Fehler; `null` = still bleiben (selbst abgebrochen). */
export const biometrieFehlerText = (f: BiometrieFehler): string | null => {
    switch (f) {
        case 'abgebrochen': return null;
        case 'gesperrt': return 'Zu viele Fehlversuche — Fingerabdruck/Gesicht ist vorübergehend gesperrt. Bitte mit Passwort anmelden.';
        case 'nicht_gespeichert': return 'Auf diesem Gerät ist keine Anmeldung gespeichert. Bitte mit Passwort anmelden.';
        case 'nicht_verfuegbar': return 'Fingerabdruck/Gesicht ist auf diesem Gerät gerade nicht verfügbar. Bitte mit Passwort anmelden.';
        case 'schluessel_ungueltig': return 'Die gespeicherte Anmeldung ist nicht mehr lesbar und wurde entfernt. Bitte mit Passwort anmelden und unter App-Info neu einrichten.';
        case 'biometrie_geaendert': return BIOMETRIE_GEAENDERT_TEXT;
        default: return 'Anmeldung mit Fingerabdruck/Gesicht nicht möglich. Bitte mit Passwort anmelden.';
    }
};

/** Hat das Plugin den Eintrag bei diesem Fehler gelöscht? Dann den Stand neu lesen (Knopf verschwindet). */
export const biometrieEintragEntfernt = (f: BiometrieFehler): boolean =>
    f === 'schluessel_ungueltig' || f === 'biometrie_geaendert';

/** Klartext beim Einrichten (App-Info); `null` = still bleiben. */
export const biometrieEinrichtenFehlerText = (f: BiometrieFehler): string | null => {
    switch (f) {
        case 'abgebrochen': return null;
        case 'gesperrt': return 'Zu viele Fehlversuche — Fingerabdruck/Gesicht ist vorübergehend gesperrt. Später erneut versuchen.';
        case 'nicht_verfuegbar': return 'Fingerabdruck/Gesicht ist auf diesem Gerät gerade nicht verfügbar.';
        default: return 'Einrichten nicht möglich — bitte erneut versuchen.';
    }
};

/**
 * Art eines Fehlers aus `signInWithPassword`. Gesperrtes Konto (Benutzerverwaltung → Sperren,
 * Migration Nr. 138): GoTrue meldet nur „User is banned“. Alles andere (Netz, Rate-Limit …) ist
 * KEIN falsches Passwort.
 */
export const anmeldeFehlerArt = (e: { message: string; code?: string }): 'falsche_daten' | 'gesperrt' | 'sonst' => {
    if (e.code === 'user_banned' || /user is banned/i.test(e.message)) return 'gesperrt';
    if (e.code === 'invalid_credentials' || /invalid login credentials/i.test(e.message)) return 'falsche_daten';
    return 'sonst';
};

/** Das gespeicherte Passwort wird vom Server abgelehnt — meist wurde es geändert. */
export const BIOMETRIE_PASSWORT_VERALTET =
    'Das gespeicherte Passwort stimmt nicht mehr (geändert?). Bitte einmal mit Passwort anmelden — danach klappt Fingerabdruck/Gesicht wieder.';

export const gleicheEmail = (a: string | null | undefined, b: string | null | undefined): boolean =>
    !!a && !!b && a.trim().toLowerCase() === b.trim().toLowerCase();

/** Knopf „Mit Fingerabdruck / Gesicht anmelden“ auf der Anmeldemaske. */
export const biometrieAnmeldungAnbieten = (stand: BiometrieStand | null): boolean =>
    !!stand && stand.verfuegbar && stand.gespeichert && !!stand.email;

/**
 * Nach einer Passwort-Anmeldung das Passwort still nachziehen — nur für die
 * gespeicherte E-Mail (das Plugin lehnt alles andere ohne Bestätigung ab).
 * So bleibt die Biometrie nach einer Passwortänderung brauchbar.
 */
export const biometrieNachziehen = (stand: BiometrieStand | null, email: string): boolean =>
    !!stand && stand.gespeichert && gleicheEmail(stand.email, email);

/** So lange nach dem Laden der Seite fragt die Anmeldemaske von selbst nach Finger/Gesicht. */
export const BIOMETRIE_AUTO_FENSTER_MS = 20_000;

/**
 * Fragt die Anmeldemaske von selbst (ohne Tipp auf den Knopf)? Höchstens einmal je Laden der Seite,
 * und nur, wenn die Maske das Erste ist, was man sieht — also nie, wenn es in diesem App-Lauf schon
 * eine Sitzung gab (`sitzungImLauf`): weder nach einem bewussten Abmelden (auch nicht in den ersten
 * Sekunden, da will man meist ein anderes Konto) noch, wenn die App schon angemeldet startete. Der
 * Knopf bleibt in beiden Fällen. Sonst:
 *  - `start`: sie erscheint kurz nach dem Laden (Kaltstart, Neuladen nach Sitzungsende);
 *  - `zurueck`: die App kommt mit offener Maske wieder in den Vordergrund.
 * Ein Sitzungsende von außen lädt die Seite neu (sessionRevocation.ts) — das ist ein neuer Lauf.
 */
export const biometrieAutomatischFragen = (l: {
    anbieten: boolean;
    schonVersucht: boolean;
    /** In diesem App-Lauf gab es schon eine Sitzung (`SitzungImLauf.gesehen`). */
    sitzungImLauf: boolean;
    anlass: 'start' | 'zurueck';
    msSeitStart: number;
}): boolean =>
    l.anbieten && !l.schonVersucht && !l.sitzungImLauf && (l.anlass === 'zurueck' || l.msSeitStart < BIOMETRIE_AUTO_FENSTER_MS);

/** Was `SitzungImLauf.beobachte` von `supabase.auth` braucht. */
export interface AuthBeobachtbar {
    onAuthStateChange?: (rueckruf: (ereignis: string, sitzung: unknown) => void) => unknown;
}

/**
 * Merker „in diesem App-Lauf gab es eine Sitzung“ — lebt so lange wie die geladene Seite.
 * `beobachte` hängt sich einmal an `onAuthStateChange`; supabase-js meldet dem neuen Abonnenten
 * zuerst `INITIAL_SESSION` mit der Sitzung beim Start, danach jede Anmeldung. `merke` setzt ihn
 * direkt (nach einer erfolgreichen Anmeldung auf der Maske, falls das Abo fehlt).
 */
export interface SitzungImLauf {
    gesehen(): boolean;
    merke(): void;
    /** Einmal je Lauf; wirft nie. Ohne `onAuthStateChange` bleibt nur `merke`. */
    beobachte(auth: AuthBeobachtbar | null | undefined): void;
}

export const sitzungImLaufMerker = (): SitzungImLauf => {
    let gesehen = false;
    let beobachtet = false;
    return {
        gesehen: () => gesehen,
        merke: () => { gesehen = true; },
        beobachte: (auth) => {
            if (beobachtet || typeof auth?.onAuthStateChange !== 'function') return;
            try {
                auth.onAuthStateChange((_ereignis, sitzung) => { if (sitzung) gesehen = true; });
                beobachtet = true;
            } catch (e) {
                console.warn('[biometrie] Sitzung nicht beobachtbar:', e);
            }
        },
    };
};

/** Was die Karte in App-Info zeigt. */
export type BiometrieKartenLage =
    | 'update_noetig'        // APK ohne Plugin
    | 'keine_hardware'       // Gerät kann es nicht
    | 'android_einrichten'   // kein Finger/Gesicht in Android hinterlegt
    | 'nicht_verfuegbar'     // vorübergehend (Sensor belegt, Sicherheitsupdate …)
    | 'aus'                  // geht, nichts gespeichert
    | 'an'                   // für das eigene Konto eingerichtet
    | 'an_anderes_konto';    // für ein anderes Konto eingerichtet

export const biometrieKartenLage = (
    pluginDa: boolean,
    stand: BiometrieStand | null,
    eigeneEmail: string | null,
): BiometrieKartenLage => {
    if (!pluginDa) return 'update_noetig';
    if (!stand) return 'nicht_verfuegbar';
    // Ein vorhandener Eintrag bleibt sichtbar (und löschbar), auch wenn der Sensor gerade fehlt.
    if (stand.gespeichert) return gleicheEmail(stand.email, eigeneEmail) ? 'an' : 'an_anderes_konto';
    if (stand.grund === 'keine_hardware') return 'keine_hardware';
    if (stand.grund === 'nicht_eingerichtet') return 'android_einrichten';
    if (!stand.verfuegbar) return 'nicht_verfuegbar';
    return 'aus';
};
